// Диалог подтверждения получения документа - на базе DocumentLayout (split-layout)

import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Typography,
    CircularProgress,
    Alert,
    Chip,
    Divider,
    Grid,
    Paper,
    LinearProgress,
    Tooltip
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
    ArrowForward as ArrowForwardIcon,
    Check as CheckIcon,
    Close as CloseIcon,
    AllInbox as AllInboxIcon,
    Inventory as InventoryIcon,
    QrCode as QrCodeIcon,
    Warning as WarningIcon,
    History,
    Visibility as VisibilityIcon,
    ArrowBack as ArrowBackIcon,
    LocalShipping as ShippingIcon,
    CheckCircle as CheckedIcon,
    PendingActions as PendingIcon,
    HelpOutline as HelpIcon
} from '@mui/icons-material';
import axios from 'axios';
import { ReceivingConfirmationDialogProps, ReceivingVerificationRow, BoxCode } from './types';
import BoxScanningView from './BoxScanningView';
import ProductVerificationView from './ProductVerificationView';
import { getDocumentNumber, formatDate, getStatusText } from './utils';
import DocumentViewDialog from './DocumentViewDialog';
import DocumentLayout, { colors } from './DocumentLayout';

const API_BASE = 'https://tbl.zerdegroup.kz/api/v1';

type Step = 'boxes' | 'products';

const getStatusColor = (status: number): string => {
    switch (status) {
        case 1: return colors.info;
        case 2: return colors.primary;
        case 3: return colors.success;
        case 4: return colors.success;
        case 5: return colors.error;
        case 6: return colors.secondary;
        case 7: return colors.info;
        case 9: return colors.warning;
        case 10: return colors.success;
        case 11: return colors.error;
        case 12: return colors.warning;
        case 13: return colors.warning;
        case 14: return colors.error;
        case 15: return colors.success;
        case 16: return colors.secondary;
        default: return '#94a3b8';
    }
};

const ReceivingConfirmationDialog: React.FC<ReceivingConfirmationDialogProps> = ({
    open,
    document,
    onClose,
    onConfirm,
    mode = 'pharmacy'
}) => {
    const [currentStep, setCurrentStep] = useState<Step>('boxes');
    const [boxes, setBoxes] = useState<BoxCode[]>([]);
    const [products, setProducts] = useState<ReceivingVerificationRow[]>([]);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [errors, setErrors] = useState<any[]>([]);
    const [viewMode, setViewMode] = useState<boolean>(false);

    // Загрузка данных при открытии диалога
    useEffect(() => {
        if (open && document) {
            loadDocumentData();
        } else {
            setCurrentStep('boxes');
            setBoxes([]);
            setProducts([]);
            setLoadError('');
            setErrors([]);
        }
    }, [open, document]);

    const loadDocumentData = async () => {
        if (!document) return;

        setLoading(true);
        setLoadError('');

        try {
            const boxesResponse = await axios.get(`${API_BASE}/documents/${document.id}/boxes`);
            if (boxesResponse.data.success) {
                const loadedBoxes: BoxCode[] = boxesResponse.data.boxes.map((box: any) => ({
                    box_num: box.box_num,
                    box_code: box.box_code,
                    scanned: false
                }));
                setBoxes(loadedBoxes);
            }

            if ((mode === 'pharmacy' || mode === 'storage-return') && document.tableData) {
                const verificationRows: ReceivingVerificationRow[] = document.tableData.map(item => ({
                    ...item,
                    actualQuantity: item.Quantity,
                    // Для storage-return: КМ заполняются автоматически для визуальной сверки (без ручного ввода)
                    actualMarkCodes: mode === 'storage-return'
                        ? (item.markCodes ? item.markCodes.map(mc => mc.mark) : [])
                        : (item.markCodes ? new Array(item.markCodes.length).fill('') : [])
                }));
                setProducts(verificationRows);
            }
        } catch (err: any) {
            console.error('Ошибка при загрузке данных документа:', err);
            setLoadError(err.response?.data?.message || 'Ошибка при загрузке данных документа');
        } finally {
            setLoading(false);
        }
    };

    const handleBoxScanned = (boxNum: number, code: string) => {
        setBoxes(prevBoxes =>
            prevBoxes.map(box =>
                box.box_num === boxNum
                    ? { ...box, scanned: true, scannedAt: new Date() }
                    : box
            )
        );
    };

    const handleProductUpdate = (productId: string, field: 'actualQuantity' | 'actualMarkCodes', value: string | string[]) => {
        setProducts(prevProducts =>
            prevProducts.map(product =>
                product.id === productId
                    ? { ...product, [field]: value }
                    : product
            )
        );
    };

    const areAllBoxesScanned = () => boxes.every(box => box.scanned);

    const areAllProductsValid = () => {
        return products.every(product => {
            if (!product.markCodes || product.markCodes.length === 0) return true;
            const filledCodes = product.actualMarkCodes?.filter(code => code.trim() !== '') || [];
            return filledCodes.length === product.markCodes.length;
        });
    };

    const canProceed = () => {
        if (currentStep === 'boxes') return areAllBoxesScanned();
        if (currentStep === 'products') {
            if (mode === 'storage-return') return true; // Все данные отображаются только для визуальной сверки
            return areAllProductsValid();
        }
        return false;
    };

    const handleNext = () => {
        if (currentStep === 'boxes') {
            if (mode === 'storage') {
                handleSubmit();
            } else {
                setCurrentStep('products');
            }
        }
    };

    const handleBack = () => {
        if (currentStep === 'products') {
            setCurrentStep('boxes');
        }
    };

    const handleReject = async () => {
        if (!document) return;

        setRejecting(true);
        setErrors([]);

        try {
            const currentUsername = localStorage.getItem('username') || '';
            const response = await axios.post(`${API_BASE}/documents/${document.id}/reject`, { username: currentUsername });

            if (response.data.success) {
                onConfirm();
                onClose();
            }
        } catch (err: any) {
            console.error('Ошибка при отклонении документа:', err);
            setErrors([err.response?.data?.message || 'Ошибка при отклонении документа']);
        } finally {
            setRejecting(false);
        }
    };

    const handleSubmit = async () => {
        if (!document) return;

        setSubmitting(true);
        setErrors([]);

        try {
            let response;

            if (mode === 'storage') {
                const currentUsername = localStorage.getItem('username') || '';
                response = await axios.post(`${API_BASE}/documents/${document.id}/storage-confirm`, { username: currentUsername });
            } else if (mode === 'storage-return') {
                const scannedBoxes = boxes.filter(box => box.scanned).map(box => box.box_code);
                const currentUsername = localStorage.getItem('username') || '';
                response = await axios.post(`${API_BASE}/documents/${document.id}/confirm-receipt`, {
                    scannedBoxes,
                    isStorageReturn: true,
                    username: currentUsername
                });
            } else {
                const scannedBoxes = boxes.filter(box => box.scanned).map(box => box.box_code);
                const productsData = products.map(product => ({
                    tbl_Zid: product.id,
                    actualQuantity: product.actualQuantity,
                    markCodes: product.actualMarkCodes?.filter(code => code.trim() !== '') || []
                }));
                const currentUsername = localStorage.getItem('username') || '';
                response = await axios.post(`${API_BASE}/documents/${document.id}/confirm-receipt`, {
                    scannedBoxes,
                    products: productsData,
                    username: currentUsername
                });
            }

            if (response.data.success) {
                onConfirm();
                onClose();
            }
        } catch (err: any) {
            console.error('Ошибка при подтверждении получения:', err);
            const serverErrors = err.response?.data?.errors;
            if (serverErrors && Array.isArray(serverErrors) && serverErrors.length > 0) {
                setErrors(serverErrors);
            } else {
                setErrors([err.response?.data?.message || 'Ошибка при подтверждении получения']);
            }
        } finally {
            setSubmitting(false);
        }
    };

    const getTotalMarkCodes = () => {
        return products.reduce((sum, p) => sum + (p.markCodes?.length || 0), 0);
    };

    const getScannedMarkCodes = () => {
        return products.reduce((sum, p) =>
            sum + (p.actualMarkCodes?.filter(c => c.trim() !== '').length || 0), 0
        );
    };

    if (!document) return null;

    // ========== Режим просмотра ==========
    if (viewMode) {
        const viewHeaderActions = (
            <Button
                variant="contained"
                size="small"
                onClick={() => setViewMode(false)}
                startIcon={<ArrowBackIcon />}
                sx={{
                    bgcolor: colors.warning,
                    color: '#fff',
                    fontWeight: 700,
                    textTransform: 'none',
                    '&:hover': { bgcolor: alpha(colors.warning, 0.85) }
                }}
            >
                Вернуться к приёмке
            </Button>
        );

        return (
            <DocumentViewDialog
                open={open}
                document={document}
                onClose={onClose}
                headerActionsExtra={viewHeaderActions}
            />
        );
    }

    // ========== Прогресс ==========
    const scannedBoxesCount = boxes.filter(b => b.scanned).length;
    const totalBoxes = boxes.length;
    const boxProgress = totalBoxes > 0 ? (scannedBoxesCount / totalBoxes) * 100 : 0;
    const totalMarks = getTotalMarkCodes();
    const scannedMarks = getScannedMarkCodes();
    const markProgress = totalMarks > 0 ? (scannedMarks / totalMarks) * 100 : 0;

    const statusHistory = (document as any)?.statusHistory || [];

    // ========== Header badges ==========
    const headerBadges = (
        <>
            {/* Текущий шаг */}
            <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1,
                bgcolor: alpha(colors.primary, 0.1), px: 2, py: 0.5, borderRadius: 2
            }}>
                {currentStep === 'boxes' ? (
                    <AllInboxIcon sx={{ fontSize: 18, color: colors.primary }} />
                ) : (
                    <InventoryIcon sx={{ fontSize: 18, color: colors.primary }} />
                )}
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: colors.dark }}>
                    {currentStep === 'boxes' ? 'Сканирование коробов' : (mode === 'storage-return' ? 'Сверка товаров' : 'Проверка товаров')}
                </Typography>
            </Box>

            {/* Прогресс коробов */}
            <Chip
                icon={<AllInboxIcon style={{ fontSize: 14 }} />}
                label={`${scannedBoxesCount}/${totalBoxes}`}
                size="small"
                color={areAllBoxesScanned() ? 'success' : 'default'}
                variant={areAllBoxesScanned() ? 'filled' : 'outlined'}
                sx={{ fontWeight: 600 }}
            />

            {/* Прогресс маркировки */}
            {totalMarks > 0 && (
                <Chip
                    icon={<QrCodeIcon style={{ fontSize: 14 }} />}
                    label={`${scannedMarks}/${totalMarks}`}
                    size="small"
                    color={areAllProductsValid() ? 'success' : 'default'}
                    variant={areAllProductsValid() ? 'filled' : 'outlined'}
                    sx={{ fontWeight: 600 }}
                />
            )}

            {/* Режим маркировки для возврата */}
            {(document?.state === 4 || document?.type === 4 || mode === 'storage-return') && (
                <Chip
                    label={
                        document?.requestMarks === 1 || document?.isMarkedProduct
                            ? 'Маркированный возврат'
                            : 'Возврат без маркировки'
                    }
                    size="small"
                    color={document?.requestMarks === 1 || document?.isMarkedProduct ? 'primary' : 'default'}
                    sx={{ fontWeight: 600, borderRadius: 1 }}
                />
            )}
        </>
    );

    // ========== Header actions: Просмотр (яркий) ==========
    const headerActions = (
        <Button
            variant="contained"
            size="small"
            startIcon={<VisibilityIcon />}
            onClick={() => setViewMode(true)}
            sx={{
                bgcolor: colors.info,
                color: '#fff',
                fontWeight: 700,
                textTransform: 'none',
                boxShadow: `0 2px 8px ${alpha(colors.info, 0.4)}`,
                '&:hover': { bgcolor: alpha(colors.info, 0.85), boxShadow: `0 4px 12px ${alpha(colors.info, 0.5)}` }
            }}
        >
            Просмотр документа
        </Button>
    );

    // ========== Sidebar ==========
    const sidebarContent = (
        <>
            <Box sx={{ p: 3 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Отправитель + Получатель */}
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Отправитель</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>{document.fromPharmacy}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Получатель</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>{document.toPharmacy}</Typography>
                        </Grid>
                    </Grid>

                    <Divider sx={{ borderStyle: 'dashed' }} />

                    {/* Тип + Маршрут */}
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Тип документа</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>{document.typeName}</Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Маршрут</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>
                                {document.typeMovement === 2 ? 'Через РЦ' : 'Прямое'}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Режим маркировки для возврата */}
                    {(document.state === 4 || document.type === 4 || mode === 'storage-return') && (
                        <>
                            <Divider sx={{ borderStyle: 'dashed' }} />
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Тип возврата</Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark }}>
                                        {document.requestMarks === 1 || document.isMarkedProduct
                                            ? 'Маркированная продукция'
                                            : 'Без обязательной маркировки'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </>
                    )}

                    <Divider sx={{ borderStyle: 'dashed' }} />

                    {/* Дата */}
                    <Box>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500, display: 'block', mb: 0.5 }}>Создан / Обновлен</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {formatDate(document.createdAtISO || document.createdAt)}
                        </Typography>
                        {document.updateDate && (
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(document.updateDateISO || document.updateDate)}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Комментарий */}
                {document.comment && (
                    <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: alpha(colors.warning, 0.05), borderColor: alpha(colors.warning, 0.2) }}>
                        <Typography variant="caption" gutterBottom sx={{ color: colors.warning, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <WarningIcon fontSize="inherit" /> КОММЕНТАРИЙ
                        </Typography>
                        <Typography variant="body2" sx={{ color: colors.dark }}>{document.comment}</Typography>
                    </Paper>
                )}
            </Box>

            <Divider />

            {/* Прогресс - коробы */}
            <Box sx={{ p: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: colors.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AllInboxIcon fontSize="small" color="disabled" />
                    Коробы ({totalBoxes})
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <LinearProgress
                        variant="determinate"
                        value={boxProgress}
                        color={areAllBoxesScanned() ? 'success' : 'primary'}
                        sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e8eaf6' }}
                    />
                    <Typography variant="caption" sx={{ fontWeight: 600, color: areAllBoxesScanned() ? colors.success : colors.dark, whiteSpace: 'nowrap' }}>
                        {scannedBoxesCount} / {totalBoxes}
                    </Typography>
                </Box>
                {areAllBoxesScanned() && (
                    <Chip label="Все коробы отсканированы" size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                )}
            </Box>

            <Divider />

            {/* Прогресс - товары и маркировка */}
            {(mode === 'pharmacy' || mode === 'storage-return') && (
                <>
                    <Box sx={{ p: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700, color: colors.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
                            <InventoryIcon fontSize="small" color="disabled" />
                            Товары ({products.length})
                        </Typography>
                        {totalMarks > 0 && (
                            <>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                                    <LinearProgress
                                        variant="determinate"
                                        value={markProgress}
                                        color={areAllProductsValid() ? 'success' : 'primary'}
                                        sx={{ flex: 1, height: 6, borderRadius: 3, bgcolor: '#e8eaf6' }}
                                    />
                                    <Typography variant="caption" sx={{ fontWeight: 600, color: areAllProductsValid() ? colors.success : colors.dark, whiteSpace: 'nowrap' }}>
                                        {scannedMarks} / {totalMarks}
                                    </Typography>
                                </Box>
                                <Typography variant="caption" color="text.secondary">Маркировочные коды</Typography>
                            </>
                        )}
                    </Box>
                    <Divider />
                </>
            )}

            {/* История статусов */}
            {statusHistory.length > 0 && (
                <Box sx={{ p: 3, flexGrow: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 700, color: colors.dark, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <History fontSize="small" color="disabled" />
                        История статусов
                    </Typography>
                    <Box sx={{ position: 'relative', pl: 1 }}>
                        <Box sx={{ position: 'absolute', top: 4, bottom: 4, left: 12, width: 2, bgcolor: colors.light }} />
                        {statusHistory.map((h: any, idx: number) => (
                            <Box key={idx} sx={{ display: 'flex', gap: 2, mb: 2.5, position: 'relative' }}>
                                <Box sx={{
                                    width: 10, height: 10, borderRadius: '50%', mt: 0.5, flexShrink: 0,
                                    bgcolor: idx === 0 ? getStatusColor(h.statusZid) : colors.border,
                                    boxShadow: idx === 0 ? `0 0 0 2px ${alpha(getStatusColor(h.statusZid), 0.3)}` : 'none',
                                    zIndex: 1
                                }} />
                                <Box>
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.dark, lineHeight: 1.2 }}>
                                        {h.statusName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                                        {h.recordDate} • {h.username}
                                    </Typography>
                                    {h.description && (
                                        <Typography variant="caption" sx={{ display: 'block', mt: 0.5, color: 'text.secondary', fontStyle: 'italic', bgcolor: colors.light, p: 0.5, borderRadius: 1 }}>
                                            {h.description}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>
                        ))}
                    </Box>
                </Box>
            )}
        </>
    );

    return (
        <DocumentLayout
            open={open}
            onClose={onClose}
            title="ПРИЁМКА ДОКУМЕНТА"
            subtitle={`#${getDocumentNumber(document)}`}
            headerBadges={headerBadges}
            headerActions={headerActions}
            sidebar={sidebarContent}
            loading={loading}
        >
            {loadError ? (
                <Box sx={{ p: 3 }}>
                    <Alert severity="error">{loadError}</Alert>
                </Box>
            ) : (
                <Box sx={{ flexGrow: 1, overflow: 'auto', minHeight: 0 }}>
                    {currentStep === 'boxes' && (
                        <BoxScanningView
                            boxes={boxes}
                            onBoxScanned={handleBoxScanned}
                            onComplete={handleNext}
                            onBack={onClose}
                            mode="receiving"
                            showFooter={false}
                        />
                    )}

                    {currentStep === 'products' && (
                        <ProductVerificationView
                            products={products}
                            onProductUpdate={handleProductUpdate}
                            readOnly={mode === 'storage-return'}
                            selfCheck={mode === 'storage-return'}
                            errorProductIds={new Set(errors.filter((e: any) => e?.tbl_Zid).map((e: any) => e.tbl_Zid.toString()))}
                        />
                    )}
                </Box>
            )}

            {/* Блок ошибок */}
            {errors.length > 0 && (
                <Alert
                    severity="error"
                    onClose={() => setErrors([])}
                    sx={{ flexShrink: 0 }}
                >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Обнаружены расхождения:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 1.5 }}>
                        {errors.map((e: any, i: number) => (
                            <Typography key={i} variant="body2" sx={{ fontWeight: 600 }}>
                                • {typeof e === 'string' ? e : `${e.productName}: ${e.message}`}
                            </Typography>
                        ))}
                    </Box>
                    <Box sx={{ bgcolor: 'rgba(255,152,0,0.15)', borderRadius: 1, p: 1 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#e65100' }}>
                            Удалите ошибочные коды (позиции подсвечены красным) и отсканируйте заново
                        </Typography>
                    </Box>
                </Alert>
            )}

            {/* Action buttons - внутри правой панели (как в DocumentDialog) */}
            <Box sx={{
                p: 2,
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                gap: 1.5,
                borderTop: `1px solid ${colors.border}`,
                bgcolor: 'white',
                flexShrink: 0
            }}>

                {/* Отказаться - прижат влево */}
                <Button
                    onClick={handleReject}
                    disabled={submitting || rejecting}
                    color="inherit"
                    startIcon={rejecting ? <CircularProgress size={16} color="inherit" /> : <CloseIcon />}
                    sx={{ color: 'text.secondary', mr: 'auto' }}
                >
                    {rejecting ? 'Отклонение...' : 'Отказаться'}
                </Button>

                {/* Назад (только на шаге товаров) */}
                {currentStep === 'products' && (
                    <Button
                        onClick={handleBack}
                        disabled={submitting || rejecting}
                        color="inherit"
                        sx={{ color: 'text.secondary' }}
                    >
                        Назад
                    </Button>
                )}

                {/* Основная кнопка */}
                {currentStep === 'boxes' ? (
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        endIcon={mode === 'storage'
                            ? (submitting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />)
                            : <ArrowForwardIcon />
                        }
                        disabled={!canProceed() || submitting}
                        color={mode === 'storage' ? 'success' : 'primary'}
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : null}
                        sx={{ px: 3, fontWeight: 'bold', boxShadow: 2 }}
                    >
                        {mode === 'storage'
                            ? (submitting ? 'Подтверждение...' : 'Подтвердить и передать')
                            : 'Далее - Товары'
                        }
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        color="success"
                        onClick={handleSubmit}
                        startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <CheckIcon />}
                        disabled={submitting || rejecting || !canProceed()}
                        sx={{ px: 3, fontWeight: 'bold', boxShadow: 2 }}
                    >
                        {submitting ? 'Подтверждение...' : (mode === 'storage-return' ? 'Принять' : 'Подтвердить приёмку')}
                    </Button>
                )}
            </Box>
        </DocumentLayout>
    );
};

export default ReceivingConfirmationDialog;
